import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
// Constants - functions
import { WEB_SERVICE_MIMS } from "../../config/constants";
// Components
import UseFetch from "../../config/useFetch";
import Loader from "../loader";
import PatientNavigation from "../patientNavigation";
import PendingActions from "./sections/pendingActions";
import PatientData from "./sections/patientData";
import PatientModel from "./sections/patientModel";
import Diagnostics from "./sections/diagnostics";
import WellBeingAssessment from "./sections/wellBeingAssessment";
import TreatmentEfficacy from "./sections/treatmentEfficacy";
import Activities from "./sections/activities";
import Notes from "./sections/notes";

const Recsys = () => {
  const [isLoadingPatientData, setIsLoadingPatientData] = useState(true);
  const [patientData, setPatientData] = useState({});
  const [trainingHasStarted, setTrainingHasStarted] = useState(false);
  // Clinical scores
  const [clinicalScores, setClinicalScores] = useState([]);
  const [isLoadingClinicalScores, setIsLoadingClinicalScores] = useState(true);
  const [currentClinicalScore, setCurrentClinicalScore] = useState({});
  const [showEditClinicalScore, setShowEditClinicalScore] = useState(false);
  const [refreshClinicalScores, setRefreshClinicalScores] = useState(false);
  // Intrinsic dates
  const [intrinsicDates, setIntrinsicDates] = useState([]);
  const [isLoadingIntrinsicDates, setIsLoadingIntrinsicDates] = useState(true);
  const [newIntrinsicDate, setNewIntrinsicDate] = useState("");
  const [newMeasureKey, setNewMeasureKey] = useState("");
  const [showIntrinsicDatePopup, setShowIntrinsicDatePopup] = useState(false);
  const [refreshIntrinsicDates, setRefreshIntrinsicDates] = useState(false);
  // Diagnostic dates
  const [prescribedDayThisWeek, setPrescribedDayThisWeek] = useState("");
  const [diagnosticDates, setDiagnosticDates] = useState([]);
  const [refreshDiagnosticDates, setRefreshDiagnosticDates] = useState(false);
  const [newDiagnosticDate, setNewDiagnosticDate] = useState("");
  const [diagnosticNumber, setDiagnosticNumber] = useState(0);
  const [diagnosticId, setDiagnosticId] = useState(0);
  const [showDiagnosticDatePopup, setShowDiagnosticDatePopup] = useState(false);
  // Prescriptions
  const [isLoadingPrescriptions, setIsLoadingPrescriptions] = useState(true);
  const [isSomePrescriptionInserted, setIsSomePrescriptionInserted] = useState(false);
  const [isShowingPrescriptionsPopup, setIsShowingPrescriptionsPopup] = useState(false);
  const activitiesRef = useRef(null);
  // Inactive days
  const [inactiveDays, setInactiveDays] = useState(0);

  const { patientId } = useParams();

  // Fetch patient data
  useEffect(() => {
    UseFetch("get", `${WEB_SERVICE_MIMS}/patients/fetch/${patientId}`)
      .then(({ data }) => {
        setPatientData(data);
        setIsLoadingPatientData(false);
      })
      .catch((error) => {
        console.log("Get patient data: ", error);
      });

    return () => {
      setPatientData({});
      setIsLoadingPatientData(true);
    };
  }, [patientId]);

  // Check if the training has started
  useEffect(() => {
    if (patientData?.aisnData?.ROUTINE_STARTING_DATE) {
      const trainingStartingDate = moment(patientData.aisnData.ROUTINE_STARTING_DATE, "DD/MM/YYYY");
      setTrainingHasStarted(moment().isSameOrAfter(trainingStartingDate, "day"));
    }

    return () => {
      setTrainingHasStarted(false);
    };
  }, [patientData]);

  // Fetch Clinical Scores
  useEffect(() => {
    setIsLoadingClinicalScores(true);

    UseFetch("get", `${WEB_SERVICE_MIMS}/recsys/get-clinical-scores/${patientId}`)
      .then(({ data }) => {
        setClinicalScores(data);
        setIsLoadingClinicalScores(false);
      })
      .catch((error) => {
        console.log("Get Clinical Scores: ", error);
      });

    return () => {
      setClinicalScores([]);
      setIsLoadingClinicalScores(true);
    };
  }, [patientId, refreshClinicalScores]);

  // Fetch Intrinsic Dates
  useEffect(() => {
    setIsLoadingIntrinsicDates(true);

    UseFetch("get", `${WEB_SERVICE_MIMS}/recsys/get-intrinsic-dates/${patientId}`)
      .then(({ data }) => {
        setIntrinsicDates(data);
        setIsLoadingIntrinsicDates(false);
      })
      .catch((error) => {
        console.log("Get Intrinsic dates: ", error);
      });

    return () => {
      setIntrinsicDates([]);
      setIsLoadingIntrinsicDates(true);
    };
  }, [patientId, refreshIntrinsicDates]);

  // Fetch Diagnostic dates
  useEffect(() => {
    if (patientData?.aisnData?.AISN_GROUP !== "Control") {
      UseFetch("get", `${WEB_SERVICE_MIMS}/recsys/get-diagnostic-dates/${patientId}`)
        .then(({ data }) => {
          setPrescribedDayThisWeek(data.prescribedDayThisWeek);
          setDiagnosticDates(data.diagnostics);
        })
        .catch((error) => {
          console.log("Get diagnostic data: ", error);
        });
    }

    return () => {
      setPrescribedDayThisWeek("");
      setDiagnosticDates([]);
    };
  }, [patientData, patientId, refreshDiagnosticDates]);

  // Fetch Inactive days
  useEffect(() => {
    if (patientData?.aisnData?.AISN_GROUP !== "Control") {
      UseFetch("get", `${WEB_SERVICE_MIMS}/recsys/get-inactive-days/${patientId}`)
        .then(({ data }) => {
          setInactiveDays(data.inactiveDays);
        })
        .catch((error) => {
          console.log("Get inactive days: ", error);
        });
    }

    return () => {
      setInactiveDays(0);
    };
  }, [patientData, patientId]);

  return (
    <>
      {isLoadingPatientData || isLoadingClinicalScores || isLoadingIntrinsicDates ? (
        <Loader height={50} />
      ) : (
        <>
          <PatientNavigation patientId={patientId} />
          <PendingActions
            patientData={patientData}
            trainingHasStarted={trainingHasStarted}
            clinicalScores={clinicalScores}
            setCurrentClinicalScore={setCurrentClinicalScore}
            setShowEditClinicalScore={setShowEditClinicalScore}
            intrinsicDates={intrinsicDates}
            setNewIntrinsicDate={setNewIntrinsicDate}
            setNewMeasureKey={setNewMeasureKey}
            setShowIntrinsicDatePopup={setShowIntrinsicDatePopup}
            diagnosticDates={diagnosticDates}
            setNewDiagnosticDate={setNewDiagnosticDate}
            setDiagnosticNumber={setDiagnosticNumber}
            setDiagnosticId={setDiagnosticId}
            setShowDiagnosticDatePopup={setShowDiagnosticDatePopup}
            isLoadingPrescriptions={isLoadingPrescriptions}
            isSomePrescriptionInserted={isSomePrescriptionInserted}
            setIsShowingPrescriptionsPopup={setIsShowingPrescriptionsPopup}
            inactiveDays={inactiveDays}
            ref={activitiesRef}
          />
          <PatientData
            patientData={patientData}
            clinicalScores={clinicalScores}
            isLoadingClinicalScores={isLoadingClinicalScores}
            currentClinicalScore={currentClinicalScore}
            setCurrentClinicalScore={setCurrentClinicalScore}
            showEditClinicalScore={showEditClinicalScore}
            setShowEditClinicalScore={setShowEditClinicalScore}
            refreshClinicalScores={refreshClinicalScores}
            setRefreshClinicalScores={setRefreshClinicalScores}
            intrinsicDates={intrinsicDates}
            isLoadingIntrinsicDates={isLoadingIntrinsicDates}
            newIntrinsicDate={newIntrinsicDate}
            setNewIntrinsicDate={setNewIntrinsicDate}
            newMeasureKey={newMeasureKey}
            setNewMeasureKey={setNewMeasureKey}
            showIntrinsicDatePopup={showIntrinsicDatePopup}
            setShowIntrinsicDatePopup={setShowIntrinsicDatePopup}
            refreshIntrinsicDates={refreshIntrinsicDates}
            setRefreshIntrinsicDates={setRefreshIntrinsicDates}
          />
          {patientData.aisnData.AISN_GROUP !== "Control" && (
            <>
              <PatientModel />
              <Diagnostics
                startingDate={patientData.aisnData.ROUTINE_STARTING_DATE}
                prescribedDayThisWeek={prescribedDayThisWeek}
                diagnosticDates={diagnosticDates}
                newDiagnosticDate={newDiagnosticDate}
                setNewDiagnosticDate={setNewDiagnosticDate}
                diagnosticNumber={diagnosticNumber}
                setDiagnosticNumber={setDiagnosticNumber}
                diagnosticId={diagnosticId}
                setDiagnosticId={setDiagnosticId}
                showDiagnosticDatePopup={showDiagnosticDatePopup}
                setShowDiagnosticDatePopup={setShowDiagnosticDatePopup}
                refreshDiagnosticDates={refreshDiagnosticDates}
                setRefreshDiagnosticDates={setRefreshDiagnosticDates}
              />
              <WellBeingAssessment />
              <TreatmentEfficacy />
              <Activities
                startingDate={patientData.aisnData.ROUTINE_STARTING_DATE}
                trainingHasStarted={trainingHasStarted}
                hasRecsys={patientData.aisnData.AISN_GROUP === "RGS+AI"}
                isLoadingPrescriptions={isLoadingPrescriptions}
                setIsLoadingPrescriptions={setIsLoadingPrescriptions}
                isSomePrescriptionInserted={isSomePrescriptionInserted}
                setIsSomePrescriptionInserted={setIsSomePrescriptionInserted}
                ref={activitiesRef}
                isShowingPrescriptionsPopup={isShowingPrescriptionsPopup}
                setIsShowingPrescriptionsPopup={setIsShowingPrescriptionsPopup}
              />
              <Notes />
            </>
          )}
        </>
      )}
    </>
  );
};

export default Recsys;
